import { EventEmitter, Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivityStateEnum, PageTransitions } from '../../enums';
import { ActivityService } from '../activity/activity.service';
import { UseAnimationService } from 'src/app/modules/use-animation/use-animation.service';
import { ActivityObjectTypes } from '../../enums/activity-object-types';
import { ActivityManifestObject } from '../../interfaces/activity-manifest-object';

@Injectable({
  providedIn: 'root',
})
export class PageTransitionService {
  private _target!: HTMLElement;
  private _pageTransition = PageTransitions.NONE;
  private _previousActivityState = -1;
  private _transitions: {
    animIn: PageTransitions;
    animOut: PageTransitions;
  }[] = [];
  private _isAnimPaneInUpwardsPlayed = false;
  popAnimationInStarted = new EventEmitter<void>();
  animationEnded = new EventEmitter<void>();
  sub!: Subscription;
  dontResetOnEnd = false;
  lastAnimWasOut = false;
  noAnimTimeout!: NodeJS.Timeout;
  isTEITransitioning = false;

  constructor(
    private activityService: ActivityService,
    private useAnimationService: UseAnimationService
  ) {}

  setup(): void {
    this.activityService.popAnimationInStarted = this.popAnimationInStarted;
    this.sub = new Subscription();
    this.sub.add(
      this.activityService.currActivityManifest.subscribe(manifest => {
        this.initTransitions(manifest);
      })
    );
    this.sub.add(
      this.activityService.advanceActivityEvent.subscribe(() => {
        const newActivityState = this.activityService.activityState;
        const prevActivityState = this._previousActivityState;
        const currentStep = this.activityService.currStep;
        const prevStep = this.activityService.prevStep;

        // handle the transition when going back to the video page
        if (
          prevActivityState > 0 &&
          prevActivityState >= newActivityState &&
          (newActivityState === ActivityStateEnum.VIDEO ||
            newActivityState === ActivityStateEnum.QUESTION)
        ) {
          let videoContainer = document.querySelector(
            '.video-container'
          ) as HTMLElement;
          if (videoContainer) {
            // fade out video player when transitioning to the next video page
            videoContainer.style.animation = PageTransitions.FADE_OUT;
            videoContainer.style.animationDuration = '0.4s';
          }
          setTimeout(() => {
            videoContainer = document.querySelector(
              '.video-container'
            ) as HTMLElement;
            if (videoContainer) {
              videoContainer.style.animation =
                prevStep < currentStep
                  ? PageTransitions.SLIDE_IN_FROM_RIGHT
                  : PageTransitions.SLIDE_IN_FROM_LEFT;
              videoContainer.style.animationDuration = '0.4s';
            }
          }, 400);
        } else if (this.useAnimationService.useAnimation$.getValue()) {
          this._pageTransition = this._transitions[currentStep].animIn;

          // after initial transition upwards, change it to NONE
          if (
            !this._isAnimPaneInUpwardsPlayed &&
            this._transitions[currentStep].animIn ===
              PageTransitions.SLIDE_IN_FROM_BOTTOM
          ) {
            this._transitions[currentStep].animIn = PageTransitions.NONE;
            this._isAnimPaneInUpwardsPlayed = true;
          }
        } else {
          // adds stability to transitions cause load times are mucking things up
          this.noAnimTimeout = setTimeout(() => {
            this._pageTransition = this._transitions[currentStep].animIn;
          }, 100);
        }
        this._previousActivityState = newActivityState;
      })
    );
  }

  private initTransitions(manifest: ActivityManifestObject[]): void {
    this._transitions = [];
    this._previousActivityState = -1;
    this._isAnimPaneInUpwardsPlayed = false;
    this.isTEITransitioning = false;
    const firstPaneInIndex = { value: -1 };

    for (let i = 0; i < manifest.length; i++) {
      const type = manifest[i].type;

      switch (type) {
        case ActivityObjectTypes.PRE_START_SCREEN:
        case ActivityObjectTypes.START_SCREEN:
          this._transitions.push({
            animIn: PageTransitions.FAST_FADE_IN,
            animOut: PageTransitions.NONE,
          });
          break;
        case ActivityObjectTypes.ENTRY_SURVEY:
          this._transitions.push({
            animIn: PageTransitions.NONE,
            animOut: PageTransitions.SLIDE_OUT_TO_LEFT,
          });
          break;
        case ActivityObjectTypes.SUCCESS_CRITERIA:
          this._transitions.push({
            animIn: PageTransitions.FAST_FADE_IN,
            animOut: PageTransitions.FAST_FADE_OUT,
          });
          break;
        case ActivityObjectTypes.VIDEO:
          this.initVideoTransitions(manifest, i, firstPaneInIndex);
          break;
        case ActivityObjectTypes.QUESTION:
          this.initQuestionTransitions(manifest, i, firstPaneInIndex);
          break;
        case ActivityObjectTypes.EXIT_SURVEY:
          this._transitions.push({
            animIn: PageTransitions.SLIDE_IN_FROM_RIGHT,
            animOut: PageTransitions.SLIDE_OUT_TO_LEFT,
          });
          break;
        case ActivityObjectTypes.RESULTS_SCREEN:
          this._transitions.push({
            animIn: PageTransitions.HIDDEN_TO_UNSET,
            animOut: PageTransitions.FADE_OUT,
          });
          break;
        default:
          this._transitions.push({
            animIn: PageTransitions.NONE,
            animOut: PageTransitions.NONE,
          });
          break;
      }
    }
  }

  private initVideoTransitions(
    manifest: ActivityManifestObject[],
    i: number,
    firstPaneInIndex: { value: number }
  ): void {
    let videoAnimIn = PageTransitions.NONE;
    let videoAnimOut = PageTransitions.NONE;
    if (firstPaneInIndex.value === -1) {
      videoAnimIn = PageTransitions.SLIDE_IN_FROM_BOTTOM;
      if (
        manifest[i + 1] &&
        manifest[i + 1].type === ActivityObjectTypes.SUCCESS_CRITERIA
      ) {
        videoAnimOut = PageTransitions.SLIDE_OUT_TO_BOTTOM;
      }
      firstPaneInIndex.value = i;
    } else {
      if (
        (manifest[i + 1] &&
          manifest[i + 1].type === ActivityObjectTypes.SUCCESS_CRITERIA) ||
        (manifest[i + 1].type !== ActivityObjectTypes.VIDEO &&
          manifest[i + 1].type !== ActivityObjectTypes.QUESTION)
      ) {
        videoAnimOut = PageTransitions.SLIDE_OUT_TO_BOTTOM;
      }
    }
    this._transitions.push({
      animIn: videoAnimIn,
      animOut: videoAnimOut,
    });
  }

  animOut(): void {
    this.lastAnimWasOut = true;
    const currentStep = this.activityService.currStep;
    this._pageTransition = this._transitions[currentStep].animOut;
  }

  get pageTransition(): PageTransitions {
    return this._pageTransition;
  }

  set target(value: HTMLElement) {
    this._target = value;

    this._target.onanimationend = (event): void => {
      if (event.target === this._target) {
        this.animationEnd();
      }
    };
  }

  get target(): HTMLElement {
    return this._target;
  }

  isNextStepDownwards(): boolean {
    return (
      this._transitions[this.activityService.currStep].animOut ===
      PageTransitions.SLIDE_OUT_TO_BOTTOM
    );
  }

  isAnimating(): boolean {
    return (
      this.pageTransition !== PageTransitions.NONE || this.isTEITransitioning
    );
  }

  animationEnd(): void {
    if (!this.dontResetOnEnd) {
      this._pageTransition = PageTransitions.NONE;
    } else {
      this.dontResetOnEnd = false;
    }

    this.animationEnded.emit();
  }

  private initQuestionTransitions(
    manifest: ActivityManifestObject[],
    i: number,
    firstPaneInIndex: { value: number }
  ): void {
    let questionAnimIn = PageTransitions.NONE;
    let questionAnimOut = PageTransitions.NONE;
    if (firstPaneInIndex.value === -1) {
      questionAnimIn = PageTransitions.SLIDE_IN_FROM_BOTTOM;
      firstPaneInIndex.value = i;
    } else {
      if (
        manifest[i + 1] &&
        manifest[i + 1].type !== ActivityObjectTypes.VIDEO &&
        manifest[i + 1].type !== ActivityObjectTypes.QUESTION
      ) {
        questionAnimOut = PageTransitions.SLIDE_OUT_TO_BOTTOM;
      }

      if (
        manifest[i - 1] &&
        manifest[i - 1].type === ActivityObjectTypes.SUCCESS_CRITERIA
      ) {
        questionAnimIn = PageTransitions.SLIDE_IN_FROM_BOTTOM;
      }
    }
    this._transitions.push({
      animIn: questionAnimIn,
      animOut: questionAnimOut,
    });
  }

  cleanup(): void {
    this.isTEITransitioning = false;
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.noAnimTimeout) {
      clearTimeout(this.noAnimTimeout);
    }
  }
}
